import { z } from 'zod';
import { multiValueFilter } from '@/features/email-accounts/search-schema';
import { ESP_GRANULARITIES, ESP_VALUES } from '@/lib/reports';

/**
 * URL search schema for the ESP filter bar. Merged into `detailSearchSchema`
 * (`routes/email-accounts.$id.tsx`) — ESP analytics used to be its own route
 * and owned these keys there; it now renders inline as `#section-esp` on the
 * account detail page, so they live on that route instead.
 *
 * The URL is the single source of truth for the whole filter bar, so a refresh,
 * a deep link or a shared link reproduces the exact same four panels.
 *
 * - `from` / `to` — INCLUSIVE `YYYY-MM-DD` bounds. Left optional (not defaulted
 *   here) because the default is "the last 15 days", which depends on today:
 *   a `.catch(...)` default would freeze at module-evaluation time. The window
 *   is resolved — and clamped to the server's 90-day cap — by
 *   `resolveEspWindow` in `esp-analytics-helpers.ts`.
 * - `esps` — multi-select over the three wire buckets (`gmail`, `outlook`,
 *   `other` — see `ESP_VALUES`), normalised by the shared
 *   `multiValueFilter` exactly like the email-accounts `status`/`provider`
 *   filters: a lone value coerces to a one-element array, unknown members are
 *   dropped, and an empty set collapses to `undefined` (= all three, which is
 *   also the API's own default) so an inactive filter leaves no URL key.
 * - `granularity` — analytics time series only; the other three routes reject
 *   it. `.catch` keeps a malformed value from erroring the route. Left
 *   OPTIONAL rather than defaulted here, and resolved to
 *   `DEFAULT_ESP_GRANULARITY` by `EspAnalyticsSection`: a `.catch(default)`
 *   makes the key always-present, and TanStack Router serialises whatever
 *   `validateSearch` returns — so every `/email-accounts/$id` link in the app
 *   would grow a `?granularity=daily` nobody chose.
 */
export const espAnalyticsSearchSchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  esps: multiValueFilter(ESP_VALUES),
  granularity: z.enum(ESP_GRANULARITIES).optional().catch(undefined),
});

export type EspAnalyticsSearch = z.infer<typeof espAnalyticsSearchSchema>;
